import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  AlertTriangle,
  Server,
  Database,
  Monitor,
  HardDrive,
  Cpu,
  Network,
  Upload,
  Grid,
  List,
  Settings
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useMultipleCodelists } from '../hooks/useCodelist';
import { useAssetsPreferences } from '../hooks/usePreferences';
import { api } from '../lib/api';
import AssetForm from '../components/assets/AssetForm';
import CsvImportDialog from '../components/assets/CsvImportDialog';
import AssetsTable from '../components/assets/AssetsTable';
import ColumnConfig from '../components/assets/ColumnConfig';
import { Asset } from '../types';

const AssetsPage: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create');
  const [showForm, setShowForm] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  
  // Bulk selection state
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<number>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState<'none' | 'page' | 'all'>('none');
  const [totalFilteredAssets, setTotalFilteredAssets] = useState(0);
  
  // User preferences for view mode and table configuration
  const { 
    viewMode, 
    visibleColumns, 
    setViewMode, 
    setVisibleColumns, 
    isReady: preferencesReady 
  } = useAssetsPreferences();
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  
  // Sorting and filtering
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>({
    key: null,
    direction: 'asc'
  });
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const columnFiltersRef = useRef<Record<string, string>>({});
  const [shouldRestoreColumnFilter, setShouldRestoreColumnFilter] = useState<{
    restore: boolean;
    column: string;
    position: number;
  }>({ restore: false, column: '', position: 0 });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });
  const paginationRef = useRef(pagination);

  // Keep refs in sync with state
  useEffect(() => {
    columnFiltersRef.current = columnFilters;
  }, [columnFilters]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState<{
    restore: boolean;
    position: number;
  }>({ restore: false, position: 0 });
  const { toast } = useToast();
  
  // Load codelists for display names and filter options
  const { getItemName, getCodelistItems } = useMultipleCodelists(['nis2_chapters', 'nis2_articles', 'nis2_letters', 'asset_types_nis2', 'security_controls', 'asset_categories']);

  const loadAssets = useCallback(async (currentPage?: number) => {
    // Don't load assets until preferences are ready
    if (!preferencesReady) {
      return;
    }
    
    try {
      // Save current cursor position and focus state for search input
      const activeElement = document.activeElement;
      const isSearchFocused = activeElement === searchInputRef.current;
      const searchCursorPosition = isSearchFocused ? (searchInputRef.current?.selectionStart || 0) : 0;
      
      // Check if a column filter input is focused
      let columnFilterFocus = null;
      if (activeElement && activeElement.tagName === 'INPUT') {
        const filterInput = activeElement as HTMLInputElement;
        const columnName = filterInput.getAttribute('data-column');
        if (columnName && columnFiltersRef.current.hasOwnProperty(columnName)) {
          columnFilterFocus = {
            column: columnName,
            position: filterInput.selectionStart || 0
          };
        }
      }
      
      if (isSearchFocused) {
        console.log('Search input is focused before API call, cursor at position:', searchCursorPosition);
      }
      
      if (columnFilterFocus) {
        console.log('Column filter is focused before API call:', columnFilterFocus);
      }
      
      setLoading(true);
      
      // Use provided page or current pagination page
      const pageToLoad = currentPage !== undefined ? currentPage : paginationRef.current.page;
      
      console.log('=== loadAssets called ===');
      console.log('currentPage parameter:', currentPage);
      console.log('pagination.page state:', pagination.page);
      console.log('pageToLoad (will be sent to API):', pageToLoad);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('asset_category', selectedCategory);
      if (selectedStatus) params.append('status', selectedStatus);
      
      // Add column filters
      const activeColumnFilters = Object.fromEntries(
        Object.entries(columnFiltersRef.current).filter(([_, value]) => value.trim())
      );
      if (Object.keys(activeColumnFilters).length > 0) {
        params.append('column_filters', JSON.stringify(activeColumnFilters));
      }
      
      // Add sorting
      if (sortConfig.key) {
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
      } else {
        params.append('sortBy', 'updated_at');
        params.append('sortOrder', 'desc');
      }
      
      params.append('page', pageToLoad.toString());
      params.append('limit', paginationRef.current.limit.toString());

      console.log('API URL:', `/assets?${params.toString()}`);

      const response = await api.get<{ 
        success: boolean; 
        data: Asset[]; 
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
          hasNext: boolean;
          hasPrev: boolean;
        }
      }>(`/assets?${params.toString()}`);
      
      console.log('API response pagination:', response.pagination);
      
      if (response.success) {
        // Set focus restoration state before updating React state
        if (isSearchFocused) {
          setShouldRestoreFocus({ restore: true, position: searchCursorPosition });
        }
        
        if (columnFilterFocus) {
          setShouldRestoreColumnFilter({ 
            restore: true, 
            column: columnFilterFocus.column,
            position: columnFilterFocus.position 
          });
        }
        
        console.log('Updating React state with:', {
          assetsCount: response.data.length,
          newPagination: response.pagination
        });
        
        setAssets(response.data);
        setPagination(response.pagination);
        setTotalFilteredAssets(response.pagination.total);
      }
    } catch (error) {
      console.error('Failed to load assets:', error);
      toast({
        title: "Error",
        description: "Failed to load assets",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, selectedStatus, sortConfig, toast, preferencesReady, pagination.page]);

  // Load initial data when preferences are ready
  useEffect(() => {
    if (preferencesReady) {
      loadAssets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesReady]);

  // Debounced search function
  const debouncedLoadAssets = useCallback(() => {
    const timeout = setTimeout(() => {
      console.log('Debounced search - resetting to page 1');
      loadAssets(1); // Always reset to first page for search
    }, 500);

    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory, selectedStatus, columnFilters, sortConfig]);


  // Restore focus after React re-render
  useLayoutEffect(() => {
    if (shouldRestoreFocus.restore && searchInputRef.current) {
      console.log('Restoring focus to search input at position:', shouldRestoreFocus.position);
      
      // Try multiple methods to ensure focus is restored
      const restoreFocus = () => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.setSelectionRange(shouldRestoreFocus.position, shouldRestoreFocus.position);
        }
      };
      
      // Immediate attempt
      restoreFocus();
      
      // Backup attempts with different timing
      setTimeout(restoreFocus, 0);
      setTimeout(restoreFocus, 10);
      requestAnimationFrame(restoreFocus);
      
      setShouldRestoreFocus({ restore: false, position: 0 });
      
      // Check if focus was actually restored
      setTimeout(() => {
        console.log('Focus check after restoration, active element:', document.activeElement === searchInputRef.current);
      }, 20);
    }
  }, [shouldRestoreFocus, assets]); // Trigger after assets update

  // Restore focus for column filter inputs after React re-render
  useLayoutEffect(() => {
    if (shouldRestoreColumnFilter.restore && shouldRestoreColumnFilter.column) {
      console.log('Restoring focus to column filter:', shouldRestoreColumnFilter.column, 'at position:', shouldRestoreColumnFilter.position);
      
      const restoreColumnFilterFocus = () => {
        const filterInput = document.querySelector(`input[data-column="${shouldRestoreColumnFilter.column}"]`) as HTMLInputElement;
        if (filterInput) {
          filterInput.focus();
          filterInput.setSelectionRange(shouldRestoreColumnFilter.position, shouldRestoreColumnFilter.position);
          
          // Trigger focus event to update hasFocus state in TableHead
          filterInput.dispatchEvent(new FocusEvent('focus', { bubbles: true }));
        }
      };
      
      // Multiple attempts to ensure focus is restored
      restoreColumnFilterFocus();
      setTimeout(restoreColumnFilterFocus, 0);
      setTimeout(restoreColumnFilterFocus, 10);
      requestAnimationFrame(restoreColumnFilterFocus);
      
      setShouldRestoreColumnFilter({ restore: false, column: '', position: 0 });
      
      // Check if focus was actually restored
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLInputElement;
        const isCorrectInput = activeElement && activeElement.getAttribute('data-column') === shouldRestoreColumnFilter.column;
        console.log('Column filter focus check after restoration:', isCorrectInput);
      }, 20);
    }
  }, [shouldRestoreColumnFilter, assets]); // Trigger after assets update

  // Load assets when filters change (immediate) - reset to page 1
  useEffect(() => {
    if (preferencesReady) {
      console.log('Filters changed - resetting to page 1');
      loadAssets(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedStatus]);

  // Load assets when sorting changes (immediate) - keep current page
  useEffect(() => {
    if (preferencesReady) {
      console.log('Sorting changed - keeping current page');
      loadAssets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig]);

  // Separate effect for debounced column filters
  useEffect(() => {
    // Check if there are any active column filters now
    const hasActiveFilters = Object.values(columnFilters).some(value => value.trim());
    
    // If no active filters, check if any column filter input has focus
    if (!hasActiveFilters) {
      // Add a small delay to allow DOM to update after blur events
      setTimeout(() => {
        const activeElement = document.activeElement;
        const isColumnFilterFocused = activeElement && 
          activeElement.tagName === 'INPUT' && 
          activeElement.getAttribute('data-column');
        
        if (isColumnFilterFocused) {
          // Skip API call - user is still typing in empty field
          return;
        }
        
        // Proceed with API call for cleared filters
        const timeout = setTimeout(() => {
          if (pagination.page !== 1) {
            setPagination(prev => ({ ...prev, page: 1 }));
            loadAssets(1);
          } else {
            loadAssets();
          }
        }, 500);
        
        // Store timeout for cleanup
        return () => clearTimeout(timeout);
      }, 10); // Small delay to allow DOM update
      
      return; // Exit early, the delayed check will handle API call
    }
    
    const timeout = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadAssets(1); // Load first page directly
      } else {
        loadAssets(); // Focus is preserved automatically in loadAssets
      }
    }, 500);

    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  // Separate effect for debounced search
  useEffect(() => {
    if (searchTerm === '') {
      // Immediate search for empty string
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadAssets(1); // Load first page directly
      } else {
        loadAssets(); // Focus is preserved automatically
      }
      return;
    }

    // Use debounced search for non-empty strings
    const cleanup = debouncedLoadAssets();
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, debouncedLoadAssets]);

  const handleDeleteAsset = async (assetId: number) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }

    try {
      const response = await api.delete<{ success: boolean; message: string }>(`/assets/${assetId}`);
      if (response.success) {
        toast({
          title: "Success",
          description: "Asset deleted successfully",
        });
        // Remove from selected assets if it was selected
        setSelectedAssetIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(assetId);
          return newSet;
        });
        loadAssets(); // Reload the list
      }
    } catch (error) {
      console.error('Failed to delete asset:', error);
      toast({
        title: "Error",
        description: "Failed to delete asset",
        variant: "destructive",
      });
    }
  };

  // Bulk selection functions
  const handleSelectAsset = (assetId: number, selected: boolean) => {
    setSelectedAssetIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(assetId);
      } else {
        newSet.delete(assetId);
      }
      return newSet;
    });
    
    // Reset select all mode when individual selection changes
    if (selectAllMode !== 'none') {
      setSelectAllMode('none');
    }
  };

  const handleSelectAll = async (mode: 'none' | 'page' | 'all') => {
    setSelectAllMode(mode);
    
    if (mode === 'none') {
      setSelectedAssetIds(new Set());
    } else if (mode === 'page') {
      // Select all assets on current page
      const currentPageAssetIds = assets.filter(asset => asset.id).map(asset => asset.id!);
      setSelectedAssetIds(new Set(currentPageAssetIds));
    } else if (mode === 'all') {
      // Fetch all filtered asset IDs from backend
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (selectedCategory) params.append('asset_category', selectedCategory);
        if (selectedStatus) params.append('status', selectedStatus);
        
        // Add column filters
        const activeColumnFilters = Object.fromEntries(
          Object.entries(columnFilters).filter(([_, value]) => value.trim())
        );
        if (Object.keys(activeColumnFilters).length > 0) {
          params.append('column_filters', JSON.stringify(activeColumnFilters));
        }
        
        // Add sorting
        if (sortConfig.key) {
          params.append('sortBy', sortConfig.key);
          params.append('sortOrder', sortConfig.direction);
        } else {
          params.append('sortBy', 'updated_at');
          params.append('sortOrder', 'desc');
        }
        
        params.append('page', '1');
        params.append('limit', totalFilteredAssets.toString()); // Get all filtered results
        
        const response = await api.get<{ 
          success: boolean; 
          data: Asset[];
        }>(`/assets?${params.toString()}`);
        
        if (response.success) {
          const allFilteredAssetIds = response.data.filter(asset => asset.id).map(asset => asset.id!);
          setSelectedAssetIds(new Set(allFilteredAssetIds));
        } else {
          console.error('Failed to fetch all asset IDs for selection');
          toast({
            title: "Chyba",
            description: "Nepodařilo se načíst všechny assets pro výběr",
            variant: "destructive",
          });
          setSelectAllMode('none'); // Reset on error
        }
      } catch (error) {
        console.error('Error fetching all assets for selection:', error);
        toast({
          title: "Chyba",
          description: "Nepodařilo se načíst všechny assets pro výběr",
          variant: "destructive",
        });
        setSelectAllMode('none'); // Reset on error
      }
    }
  };

  const clearSelection = () => {
    setSelectedAssetIds(new Set());
    setSelectAllMode('none');
  };

  const getSelectedCount = () => {
    if (selectAllMode === 'all') {
      return totalFilteredAssets;
    }
    return selectedAssetIds.size;
  };

  const handleBulkDelete = async () => {
    const selectedCount = getSelectedCount();
    
    if (selectedCount === 0) {
      toast({
        title: "Chyba",
        description: "Nejsou vybrány žádné assets pro smazání",
        variant: "destructive",
      });
      return;
    }

    if (!window.confirm(`Opravdu chcete smazat ${selectedCount} vybraných assets? Tato akce je nevratná.`)) {
      return;
    }

    try {
      // Use the already selected asset IDs (which now includes all filtered assets when selectAllMode is 'all')
      const assetIdsToDelete = Array.from(selectedAssetIds);

      const response = await api.post<{ 
        success: boolean; 
        message: string;
        data: {
          successful_deletions: Array<{ asset_id: number; deleted: boolean }>;
          failed_deletions: Array<{ asset_id: number; error: string }>;
        }
      }>('/assets/bulk-delete', {
        asset_ids: assetIdsToDelete
      });

      if (response.success) {
        const { successful_deletions, failed_deletions } = response.data;
        
        toast({
          title: "Úspěch",
          description: `${successful_deletions.length} assets bylo úspěšně smazáno${failed_deletions.length > 0 ? `, ${failed_deletions.length} se nepodařilo smazat` : ''}`,
        });

        if (failed_deletions.length > 0) {
          console.warn('Failed deletions:', failed_deletions);
        }

        // Clear selection and reload
        clearSelection();
        loadAssets();
      }
    } catch (error) {
      console.error('Failed to bulk delete assets:', error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se smazat vybrané assets",
        variant: "destructive",
      });
    }
  };

  const handleCreateAsset = () => {
    setSelectedAsset(null);
    setFormMode('create');
    setShowForm(true);
  };

  const handleEditAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    setFormMode('edit');
    setShowForm(true);
  };

  const handleViewAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    setFormMode('view');
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedAsset(null);
  };

  const handleFormSave = () => {
    loadAssets(); // Reload assets after save, focus is preserved automatically if search is focused
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    clearSelection(); // Clear selection when search changes
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    clearSelection(); // Clear selection when category changes
    // Reset to first page will happen automatically via useEffect
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    clearSelection(); // Clear selection when status changes
    // Reset to first page will happen automatically via useEffect
  };

  const handlePageChange = (newPage: number) => {
    console.log('=== handlePageChange called with newPage:', newPage);
    // Load assets for the new page - pagination state will be updated by the API response
    loadAssets(newPage);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedStatus('');
    setColumnFilters({});
    clearSelection(); // Clear bulk selection when filters change
    // Reset to first page will happen automatically via useEffect
  };

  // Sorting functionality - cycles through: none -> asc -> desc -> none
  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // Same column - cycle through states
        if (prevConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: 'asc' }; // Reset to no sorting
        }
      }
      // Different column or no current sorting - start with ascending
      return { key, direction: 'asc' };
    });
  };

  // Column filtering functionality
  const handleColumnFilter = (column: string, value: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      
      if (value.trim()) {
        // Set the filter value
        newFilters[column] = value;
      } else {
        // Remove the filter completely if empty
        delete newFilters[column];
      }
      
      return newFilters;
    });
    // Reset to first page will happen automatically via useEffect
  };

  // Clear column filter and immediately call API
  const handleClearColumnFilter = (column: string) => {
    // Remove the filter from state
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[column];
      return newFilters;
    });
    
    // Immediately call API without debounce - reset to page 1
    setTimeout(() => {
      loadAssets(1);
    }, 0);
  };


  // Use server-side filtered and sorted assets directly
  const processedAssets = assets;

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'endpoints': return <Monitor className="h-4 w-4" />;
      case 'identity_and_access': return <Server className="h-4 w-4" />;
      case 'network_and_network_security': return <Network className="h-4 w-4" />;
      case 'data': return <Database className="h-4 w-4" />;
      case 'applications': return <Cpu className="h-4 w-4" />;
      case 'systems': return <HardDrive className="h-4 w-4" />;
      default: return <Server className="h-4 w-4" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'retired': return 'outline';
      default: return 'default';
    }
  };

  const getValueAssessmentBadgeVariant = (value: string) => {
    switch (value) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'secondary';
    }
  };

  // Debug logging (only when filters are active)
  if (searchTerm || selectedCategory || selectedStatus) {
    console.log(`Assets filtering:
      Assets on current page: ${assets.length}
      Total assets in database: ${pagination.total}
      Current page: ${pagination.page} of ${pagination.pages}
      Active filters:
        - Search: "${searchTerm}"
        - Category: "${selectedCategory}"
        - Status: "${selectedStatus}"
    `);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Assets</h1>
          <p className="text-muted-foreground">
            Manage information assets and their NIS2 compliance mapping
          </p>
        </div>
        <div className="flex gap-2">
          {/* View mode toggle */}
          <div className="flex border rounded-lg">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="rounded-r-none"
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Column config button (only visible in table mode) */}
          {viewMode === 'table' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowColumnConfig(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Sloupce
            </Button>
          )}
          
          <Button onClick={handleCreateAsset}>
            <Plus className="h-4 w-4 mr-2" />
            Add Asset
          </Button>
          <Button onClick={() => setShowCsvImport(true)} variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="">All Categories</option>
                {getCodelistItems('asset_categories').map((category) => (
                  <option key={category.code} value={category.code}>
                    {category.name}
                  </option>
                ))}
              </select>
              <select
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="retired">Retired</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {getSelectedCount() > 0 && (
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">
                  {selectAllMode === 'all' 
                    ? `Vybráno všech ${totalFilteredAssets} assets`
                    : `Vybráno ${getSelectedCount()} assets`
                  }
                </span>
                {selectAllMode === 'all' && (
                  <span className="text-xs text-muted-foreground">
                    (všechny vyfiltrované assets)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Smazat vybrané
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSelection}
                >
                  Zrušit výběr
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assets Display - Cards or Table */}
      {viewMode === 'cards' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {processedAssets.map((asset) => (
            <Card key={asset.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getCategoryIcon(asset.asset_category || 'system')}
                    <CardTitle className="text-lg">{asset.name}</CardTitle>
                  </div>
                  <Badge variant={getStatusBadgeVariant(asset.status)}>
                    {asset.status}
                  </Badge>
                </div>
                <CardDescription className="line-clamp-2">
                  {asset.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Type:</span>
                    <Badge variant="outline">{getItemName('asset_types_nis2', asset.type || '')}</Badge>
                  </div>
                  {asset.asset_category && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Category:</span>
                      <Badge variant="secondary">{getItemName('asset_categories', asset.asset_category)}</Badge>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Value:</span>
                    <Badge variant={getValueAssessmentBadgeVariant(asset.value_assessment)}>
                      {asset.value_assessment}
                    </Badge>
                  </div>
                  {asset.location && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Location:</span>
                      <span className="text-right">{asset.location}</span>
                    </div>
                  )}
                  {asset.nis2_article && asset.nis2_letter && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">NIS2:</span>
                      <span className="text-right">{getItemName('nis2_articles', asset.nis2_article)} - {getItemName('nis2_letters', asset.nis2_letter)}</span>
                    </div>
                  )}
                  {asset.review_date && new Date(asset.review_date) < new Date() && (
                    <div className="flex items-center space-x-1 text-amber-600 text-sm">
                      <AlertTriangle className="h-3 w-3" />
                      <span>Review overdue</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-1 mt-4">
                  <Button variant="outline" size="sm" onClick={() => handleViewAsset(asset)}>
                    <Eye className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEditAsset(asset)}>
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => asset.id && handleDeleteAsset(asset.id)}
                    disabled={!asset.id}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <AssetsTable
              assets={processedAssets}
              onViewAsset={handleViewAsset}
              onEditAsset={handleEditAsset}
              onDeleteAsset={handleDeleteAsset}
              getItemName={getItemName}
              visibleColumns={visibleColumns}
              sortConfig={sortConfig}
              onSort={handleSort}
              columnFilters={columnFilters}
              onColumnFilter={handleColumnFilter}
              onClearColumnFilter={handleClearColumnFilter}
              selectedAssetIds={selectedAssetIds}
              selectAllMode={selectAllMode}
              totalFilteredAssets={totalFilteredAssets}
              onSelectAsset={handleSelectAsset}
              onSelectAll={handleSelectAll}
            />
          </CardContent>
        </Card>
      )}

      {/* Show filter summary */}
      {(searchTerm || selectedCategory || selectedStatus || Object.values(columnFilters).some(f => f.trim())) && (
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Aktivní filtry:</span>
              {searchTerm && <Badge variant="secondary">Hledání: "{searchTerm}"</Badge>}
              {selectedCategory && <Badge variant="secondary">Kategorie: {selectedCategory}</Badge>}
              {selectedStatus && <Badge variant="secondary">Status: {selectedStatus}</Badge>}
              {Object.entries(columnFilters).map(([column, value]) => 
                value.trim() && (
                  <Badge key={column} variant="secondary">
                    {column}: "{value}"
                  </Badge>
                )
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearAllFilters}
                className="ml-2"
              >
                Vymazat filtry
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Zobrazeno {processedAssets.length} z {assets.length} assets 
              {viewMode === 'table' && sortConfig.key && (
                <span> • Řazeno podle: {sortConfig.key} ({sortConfig.direction === 'asc' ? 'vzestupně' : 'sestupně'})</span>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {processedAssets.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Server className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No assets found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || selectedCategory || selectedStatus
                  ? `No assets match your current filters. Try clearing filters to see all ${pagination.total} assets.`
                  : "Get started by adding your first information asset."}
              </p>
              {!searchTerm && !selectedCategory && !selectedStatus ? (
                <Button onClick={handleCreateAsset}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Asset
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={clearAllFilters}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} assets
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {/* Show page numbers */}
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.pages - 4, pagination.page - 2)) + i;
                    if (pageNum > pagination.pages) return null;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Asset Form Modal */}
      <AssetForm
        asset={selectedAsset || undefined}
        isOpen={showForm}
        onClose={handleFormClose}
        onSave={handleFormSave}
        mode={formMode}
      />

      {/* CSV Import Modal */}
      <CsvImportDialog
        isOpen={showCsvImport}
        onClose={() => {
          console.log('=== AssetsPage onClose callback called ===');
          setShowCsvImport(false);
          // Reload assets when dialog closes (after import)
          loadAssets();
        }}
        onImportComplete={() => {
          console.log('=== AssetsPage onImportComplete callback called ===');
          // NEPOUŽÍVAT setShowCsvImport(false) - nechej uživatele zavřít dialog ručně
          // NEPOUŽÍVAT loadAssets() - způsobuje reset dialogu
          // loadAssets se zavolá až při zavření dialogu
        }}
      />

      {/* Column Configuration Modal */}
      <ColumnConfig
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={setVisibleColumns}
        isOpen={showColumnConfig}
        onClose={() => setShowColumnConfig(false)}
      />
    </div>
  );
};

export default AssetsPage;
